import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Michael Rodriguez",
      company: "Rodriguez HVAC Solutions",
      trade: "HVAC Specialist",
      location: "Tampa, FL",
      yearsExperience: 12,
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      quote: `ProConnect completely transformed my business. I went from spending $3,200 monthly on Google Ads and HomeAdvisor to zero marketing costs. The customers are premium - they value quality work and pay on time. My average job value increased from $1,400 to $3,200.`,
      beforeIncome: "$78,000",
      afterIncome: "$94,500",
      increase: "+$16,500",
      timeframe: "12 months",
      bestFeature: "Zero marketing costs",
      videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop",
      metrics: {
        jobsCompleted: 156,
        customerRating: 4.9,
        repeatCustomers: "78%"
      }
    },
    {
      id: 2,
      name: "Sarah Chen",
      company: "Chen Master Plumbing",
      trade: "Licensed Plumber",
      location: "St. Petersburg, FL",
      yearsExperience: 8,
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      quote: `The payment protection is a game-changer. No more chasing invoices for 60-90 days. I get paid within 24 hours of completing work. My cash flow improved dramatically, and I can focus on what I do best - quality plumbing work.`,
      beforeIncome: "$92,000",
      afterIncome: "$108,200",
      increase: "+$16,200",
      timeframe: "10 months",
      bestFeature: "24-hour payment guarantee",
      videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop",
      metrics: {
        jobsCompleted: 142,
        customerRating: 4.8,
        repeatCustomers: "82%"
      }
    },
    {
      id: 3,
      name: "David Thompson",
      company: "Thompson Electric Pro",
      trade: "Master Electrician",
      location: "Clearwater, FL",
      yearsExperience: 15,
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      quote: `Best decision I made for my business. The customers through ProConnect are educated about quality electrical work. They don't haggle on price - they choose based on expertise. My profit margins doubled because I'm not competing on price anymore.`,
      beforeIncome: "$85,000",
      afterIncome: "$103,800",
      increase: "+$18,800",
      timeframe: "14 months",
      bestFeature: "Premium customer quality",
      videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop",
      metrics: {
        jobsCompleted: 189,
        customerRating: 5.0,
        repeatCustomers: "85%"
      }
    },
    {
      id: 4,
      name: "Lisa Martinez",
      company: "Martinez Home Services",
      trade: "Multi-Trade Contractor",
      location: "Tampa Bay, FL",
      yearsExperience: 10,
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      quote: `ProConnect gave me my life back. No more late nights managing social media ads or dealing with tire-kickers. The platform brings me qualified customers who respect my time and expertise. I'm earning more while working smarter, not harder.`,
      beforeIncome: "$76,500",
      afterIncome: "$95,200",
      increase: "+$18,700",
      timeframe: "11 months",
      bestFeature: "Work-life balance",
      videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop",
      metrics: {
        jobsCompleted: 134,
        customerRating: 4.9,
        repeatCustomers: "79%"
      }
    }
  ];

  const aggregateStats = {
    totalContractors: 265,
    averageIncrease: "$15,400",
    averageRating: 4.9,
    totalJobsCompleted: 2847,
    customerSatisfaction: "96%"
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials?.length]);

  const handleTestimonialChange = (index) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentData = testimonials?.[currentTestimonial];

  return (
    <section id="testimonials" className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Success Stories from Tampa Bay Contractors
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Real contractors sharing their ProConnect experience and the financial impact 
            on their businesses.
          </p>
        </div>

        {/* Main Testimonial Display */}
        <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border shadow-testimonial mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Testimonial Content */}
            <div>
              {/* Contractor Info */}
              <div className="flex items-center mb-6">
                <img
                  src={currentData?.avatar}
                  alt={currentData?.name}
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h3 className="text-xl font-bold text-foreground">{currentData?.name}</h3>
                  <p className="text-muted-foreground">{currentData?.company}</p>
                  <div className="flex items-center mt-1">
                    <Icon name="MapPin" size={14} className="text-muted-foreground mr-1" />
                    <span className="text-sm text-muted-foreground">{currentData?.location}</span>
                    <span className="mx-2 text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">{currentData?.yearsExperience} years exp.</span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center mb-6">
                {[...Array(5)]?.map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-warning mr-1" />
                ))}
                <span className="ml-2 text-sm text-muted-foreground">Verified ProConnect Member</span>
              </div>

              {/* Quote */}
              <blockquote className="text-lg text-foreground leading-relaxed mb-6 italic">
                "{currentData?.quote}"
              </blockquote>

              {/* Best Feature Highlight */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <Icon name="Award" size={20} className="text-primary mr-3" />
                  <div>
                    <div className="font-semibold text-foreground">Favorite Feature:</div>
                    <div className="text-primary">{currentData?.bestFeature}</div>
                  </div>
                </div>
              </div>

              {/* Contractor Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">{currentData?.metrics?.jobsCompleted}</div>
                  <div className="text-xs text-muted-foreground">Jobs Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning">{currentData?.metrics?.customerRating}</div>
                  <div className="text-xs text-muted-foreground">Avg Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">{currentData?.metrics?.repeatCustomers}</div>
                  <div className="text-xs text-muted-foreground">Repeat Rate</div>
                </div>
              </div>
            </div>

            {/* Right Side - Financial Impact */}
            <div>
              {/* Video Thumbnail */}
              <div className="relative mb-8">
                <img
                  src={currentData?.videoThumbnail}
                  alt="Video testimonial"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                  <button className="w-16 h-16 bg-primary rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors">
                    <Icon name="Play" size={24} color="white" />
                  </button>
                </div>
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
                  Watch {currentData?.name}'s Story
                </div>
              </div>

              {/* Financial Impact */}
              <div className="bg-muted/50 rounded-lg p-6">
                <h4 className="font-bold text-foreground mb-4 text-center">Financial Impact</h4>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Before ProConnect:</span>
                    <span className="font-semibold text-foreground">{currentData?.beforeIncome}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">After ProConnect:</span>
                    <span className="font-bold text-primary text-lg">{currentData?.afterIncome}</span>
                  </div>
                  
                  <div className="border-t border-border pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-foreground">Annual Increase:</span>
                      <span className="font-bold text-accent text-xl">{currentData?.increase}</span>
                    </div>
                    <div className="text-center text-sm text-muted-foreground mt-2">
                      Achieved in {currentData?.timeframe}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Navigation */}
        <div className="flex justify-center mb-16">
          <div className="flex space-x-3">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => handleTestimonialChange(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentTestimonial
                    ? 'bg-primary scale-125' :'bg-muted hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Aggregate Statistics */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            ProConnect Network Statistics
          </h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">
                {aggregateStats?.totalContractors}+
              </div>
              <div className="text-sm text-muted-foreground">Active Contractors</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-accent mb-2">
                {aggregateStats?.averageIncrease}
              </div>
              <div className="text-sm text-muted-foreground">Average Increase</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-warning mb-2">
                {aggregateStats?.averageRating}
              </div>
              <div className="text-sm text-muted-foreground">Average Rating</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-secondary mb-2">
                {aggregateStats?.totalJobsCompleted?.toLocaleString()}+
              </div>
              <div className="text-sm text-muted-foreground">Jobs Completed</div>
            </div>
            
            <div>
              <div className="text-3xl font-bold text-accent mb-2">
                {aggregateStats?.customerSatisfaction}
              </div>
              <div className="text-sm text-muted-foreground">Customer Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Quick Testimonial Cards */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials?.map((testimonial, index) => (
            <button
              key={testimonial?.id}
              onClick={() => handleTestimonialChange(index)}
              className={`text-left p-4 rounded-lg border transition-all duration-200 ${
                index === currentTestimonial
                  ? 'border-primary bg-primary/5 shadow-lg'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center mb-3">
                <img
                  src={testimonial?.avatar}
                  alt={testimonial?.name}
                  className="w-10 h-10 rounded-full object-cover mr-3"
                />
                <div>
                  <div className="font-semibold text-foreground text-sm">{testimonial?.name}</div>
                  <div className="text-xs text-muted-foreground">{testimonial?.trade}</div>
                </div>
              </div>
              
              <div className="text-lg font-bold text-accent mb-1">{testimonial?.increase}</div>
              <div className="text-xs text-muted-foreground">in {testimonial?.timeframe}</div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;